<script setup lang="ts">
// Overrides @basecom-gmbh/pond/components/Cms/Element/CmsElementImage.vue
// IDS-221: add prop for custom ids "shadow"
import type { CmsElementImage } from '@shopware-pwa/composables-next';
import { buildUrlPrefix, getTranslatedProperty } from '@shopware-pwa/helpers-next';
import { useElementSize } from '@vueuse/core';
import type { CSSProperties } from 'vue';

// IDS-221: add props
const props = withDefaults(
    defineProps<{
        element: CmsElementImage;
        shadowPosition?: 'none' | 'left' | 'right';
        smallImage?: boolean;
    }>(),
    {
        shadowPosition: 'none',
        smallImage: false,
    },
);

const { getUrlPrefix } = useUrlResolver();
const imageElement = ref(null);
const { width, height } = useElementSize(imageElement);
const { getCmsMedia } = useMedia();
const { containerStyle, displayMode, imageContainerAttrs, imageAttrs, imageLink, isVideoElement, mimeType } =
    useCmsElementImage(props.element);

const { getCmsLink } = useFormatCmsLink();
const { url } = useCountrySwitch();

const { srcPath } = getCmsMedia(width, height, imageAttrs);

const imageComputedContainerAttrs = computedAsync(async() => {
    const imageAttrsCopy = Object.assign({}, imageContainerAttrs.value);
    if (imageAttrsCopy?.href) {
        imageAttrsCopy.href = buildUrlPrefix(imageAttrsCopy.href, getUrlPrefix()).path;
        let linkWithoutPrefix = imageAttrsCopy.href;
        if (linkWithoutPrefix.startsWith('/')) {
            linkWithoutPrefix = linkWithoutPrefix.substring(1);
        }
        imageAttrsCopy.href = (await getCmsLink(linkWithoutPrefix)) || imageAttrsCopy.href;
        imageAttrsCopy.href = url(imageAttrsCopy.href) as string;
    }
    return imageAttrsCopy;
}, {});

const getMinHeightAsHeight = (properties: CSSProperties) => {
    const height = properties.minHeight ?? '100%';
    return `height: ${height}`;
};

const mediaObject = props.element.data?.media;
</script>

<template>
    <component
        :is="imageLink.url ? 'a' : 'div'"
        v-if="imageAttrs.src"
        class="relative size-full"
        :style="containerStyle"
        v-bind="imageComputedContainerAttrs"
    >
        <video
            v-if="isVideoElement"
            controls
            :class="{
                'size-full': true,
                'object-cover': displayMode === 'cover',
            }"
        >
            <source
                :src="imageAttrs.src"
                :type="mimeType"
            />
            {{ $t('cms.element.videoTagNotSupported') }}
        </video>

        <!-- IDS-221: use prop for styling -->
        <div
            v-else
            class="relative"
        >
            <img
                ref="imageElement"
                v-cms-element-lazy-load="{ id: mediaObject.id, type: 'image' }"
                loading="lazy"
                :class="{
                    'size-full': true,
                    'object-cover': displayMode === 'cover',
                    'h-56 object-cover md:h-105': shadowPosition !== 'none',
                    'h-64 object-cover md:h-96': shadowPosition !== 'none' && smallImage,
                }"
                :style="displayMode === 'cover' ? getMinHeightAsHeight(containerStyle) : ''"
                :alt="getTranslatedProperty(mediaObject, 'alt')"
                :title="getTranslatedProperty(mediaObject, 'title')"
                :src="srcPath"
                :srcset="imageAttrs.srcset"
            />

            <div
                v-if="shadowPosition !== 'none'"
                class="-z-10 w-full bg-neutral-300"
                :class="{
                    'absolute -right-3 -top-3': shadowPosition === 'right',
                    'absolute -left-3 -top-3': shadowPosition === 'left',
                    'h-56 md:h-105': !smallImage,
                    'h-64 md:h-96': smallImage,
                }"
            />
        </div>
    </component>
</template>
